import { FloatingWindowController } from "../workspace/floating-window-controller.js";
import {
  UpcomingSplitsDataSource,
} from "../data/upcoming-splits-data-source.js";
import { RecentSplitsDataSource } from "../data/recent-splits-data-source.js";
import { validateDateRange } from "../data/upcoming-earnings-data-source.js";
import { calendarDateRange } from "../data/calendar-date-range.js";
import { displayCompanyName, normalizeSplitRatio } from "./calendar-display.js";
import "../styles/upcoming-splits.css";
import { VisibleFeedRefreshController } from "../data/visible-feed-refresh-controller.js";

const VERSION = 2;
const SORT_LABELS = { date: "Date", symbol: "Symbol / company", ratio: "Ratio" };
const DATE_NOTE = "Announced effective dates; execution not independently confirmed";
let windowSequence = 0;

export class UpcomingSplitsWindow {
  constructor({
    surface, launcher, endpoint = "/api/v1/upcoming-splits", recentEndpoint = "/api/v1/recent-splits",
    onOpenChart = async () => {}, announce = () => {}, onStateChange = () => {},
    onFocusWindow = () => {}, nextStackOrder = null,
    storageKey = null, restoreOnConstruct = true, refreshOptions = {}
  }) {
    if (!(surface instanceof HTMLElement)) throw new TypeError("Upcoming splits requires a workspace surface.");
    if (!(launcher instanceof HTMLButtonElement)) throw new TypeError("Upcoming splits requires a launcher button.");
    this.surface = surface;
    this.launcher = launcher;
    this.storageKey = storageKey;
    this.onOpenChart = onOpenChart;
    this.announce = announce;
    this.onStateChange = onStateChange;
    this.onFocusWindow = onFocusWindow;
    this.nextStackOrder = nextStackOrder;
    this.dataSource = new UpcomingSplitsDataSource({ endpoint });
    this.recentDataSource = new RecentSplitsDataSource({ endpoint: recentEndpoint });
    this.tabIdPrefix = `chart-splits-${++windowSequence}`;
    this.abortController = new AbortController();
    this.request = null;
    this.requestSequence = 0;
    this.disposed = false;
    this.opened = false;
    this.loading = false;
    this.backgroundLoading = false;
    this.loadedRange = null;
    this.items = [];
    this.metadata = null;
    this.pendingCharts = new Set();
    this.savedState = readState(storageKey);
    this.assignPreferences(this.savedState);
    this.stackOrder = validStack(this.savedState.stackOrder) ? this.savedState.stackOrder : 1;
    this.element = this.createElement();
    this.element.style.zIndex = String(this.stackOrder);
    this.floating = new FloatingWindowController({
      surface, windowElement: this.element,
      dragHandle: this.element.querySelector("[data-upcoming-splits-drag]"),
      resizeHandles: this.element.querySelectorAll("[data-window-resize]"),
      minimumWidth: 320, minimumHeight: 480,
      onInteractionEnd: () => this.saveState()
    });
    this.feedRefresh = new VisibleFeedRefreshController({
      ...refreshOptions,
      refresh: ({ now }) => this.refreshStoredFeed(now)
    });
    this.bind();
    this.syncControls();
    if (restoreOnConstruct && this.savedState.open) this.open({ restoreFocus: false });
  }

  createElement() {
    const element = document.createElement("section");
    element.className = "chart-floating-window upcoming-earnings-window upcoming-splits-window";
    element.dataset.upcomingSplitsWindow = "";
    element.dataset.windowSnapId = "upcoming-splits";
    element.setAttribute("role", "dialog");
    element.setAttribute("aria-label", "Splits");
    element.hidden = true;
    element.innerHTML = `
      <header class="chart-window-titlebar upcoming-earnings-titlebar" data-upcoming-splits-drag>
        <strong>Splits</strong>
        <button type="button" class="upcoming-earnings-close" data-upcoming-splits-close aria-label="Close splits" title="Close">&times;</button>
      </header>
      <div class="earnings-tabs" role="tablist" aria-label="Splits period">
        ${["upcoming", "recent"].map((tab) => `<button type="button" role="tab" id="${this.tabIdPrefix}-${tab}" aria-controls="${this.tabIdPrefix}-panel" data-splits-tab="${tab}">${tab === "upcoming" ? "Upcoming" : "Recent"}</button>`).join("")}
      </div>
      <div class="earnings-tab-panel" role="tabpanel" id="${this.tabIdPrefix}-panel" data-splits-panel>
      <div class="upcoming-earnings-summary"><span data-upcoming-splits-count role="status" aria-live="polite"></span><span>Dates follow the New York market calendar</span></div>
      <div class="upcoming-earnings-state" data-upcoming-splits-state role="status"><strong data-upcoming-splits-state-title></strong><p data-upcoming-splits-state-message></p><button type="button" data-upcoming-splits-retry hidden>Retry</button></div>
      <div class="upcoming-earnings-table-scroll" data-upcoming-splits-table-scroll hidden tabindex="0" aria-label="Upcoming splits results">
        <table class="upcoming-earnings-table" data-upcoming-splits-table>
          <caption class="chart-sr-only" data-splits-caption>${DATE_NOTE}. Ratios show new shares to old shares. Open a symbol to view its chart.</caption>
          <thead><tr>${Object.entries(SORT_LABELS).map(([key, label]) => `<th scope="col" data-upcoming-splits-sort-header="${key}" aria-sort="none"><button type="button" data-upcoming-splits-sort="${key}" aria-label="Sort by ${label}">${label}<span aria-hidden="true"></span></button></th>`).join("")}</tr></thead>
          <tbody data-upcoming-splits-rows></tbody>
        </table>
      </div>
      <p class="upcoming-earnings-action-error" data-upcoming-splits-action-error role="alert" hidden></p>
      </div>
      ${["n", "e", "s", "w", "ne", "nw", "se", "sw"].map((edge) => `<span class="chart-resize-handle chart-resize-${edge}" data-window-resize="${edge}" aria-hidden="true"></span>`).join("")}
    `;
    this.surface.append(element);
    return element;
  }

  bind() {
    const options = { signal: this.abortController.signal };
    this.launcher.addEventListener("click", () => this.open(), options);
    this.element.querySelector("[data-upcoming-splits-close]").addEventListener("click", () => this.close(), options);
    this.element.addEventListener("pointerdown", () => this.focus(), { ...options, capture: true });
    this.element.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault(); this.close();
    }, options);
    for (const tab of this.element.querySelectorAll("[data-splits-tab]")) {
      tab.addEventListener("click", () => this.selectTab(tab.dataset.splitsTab), options);
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === "Home" ? "upcoming" : event.key === "End" ? "recent"
          : tab.dataset.splitsTab === "upcoming" ? "recent" : "upcoming";
        this.selectTab(next);
      }, options);
    }
    this.element.querySelector("[data-upcoming-splits-retry]").addEventListener("click", () => this.load(), options);
    this.element.addEventListener("click", (event) => {
      const sort = event.target.closest("[data-upcoming-splits-sort]");
      if (sort) {
        const key = sort.dataset.upcomingSplitsSort;
        this.sortDirection = this.sortKey === key && this.sortDirection === "asc" ? "desc" : "asc";
        this.sortKey = key; this.renderResults(); this.saveState();
      }
      const chart = event.target.closest("[data-upcoming-splits-chart]");
      if (chart) this.openChart(chart.dataset.upcomingSplitsChart, chart);
    }, options);
  }

  open({ restoreFocus = true } = {}) {
    if (this.disposed) return;
    if (!this.opened) {
      this.element.hidden = false; this.opened = true;
      this.launcher.setAttribute("aria-pressed", "true");
      this.floating.applyGeometry(validGeometry(this.savedState.geometry) ? this.savedState.geometry : this.defaultGeometry());
      this.floating.constrainToSurface();
      Object.assign(this, calendarDateRange(this.tab, this.feedRefresh.instant()));
      this.syncControls();
      this.feedRefresh.start();
      this.load();
      this.announce("Splits opened.");
    }
    this.focus(); this.saveState();
    if (restoreFocus) this.element.querySelector(`[data-splits-tab="${this.tab}"]`).focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.opened) return;
    this.savedState.geometry = this.floating.snapshot();
    this.opened = false; this.element.hidden = true;
    this.element.classList.remove("is-focused");
    this.launcher.setAttribute("aria-pressed", "false");
    this.feedRefresh.stop();
    this.request?.abort(); this.requestSequence += 1; this.loading = false;
    this.saveState();
    if (restoreFocus) this.launcher.focus({ preventScroll: true });
    this.announce("Splits closed.");
  }

  focus() {
    if (!this.opened || this.disposed) return;
    this.onFocusWindow(this);
    const next = this.nextStackOrder?.();
    if (validStack(next)) this.stackOrder = next;
    this.element.style.zIndex = String(this.stackOrder);
    this.element.classList.add("is-focused");
    this.saveState();
  }

  refreshStoredFeed(now) {
    if (!this.opened || this.disposed || this.loading || !this.feedRefresh.visible()) return;
    const dates = calendarDateRange(this.tab, now);
    if (dates.from !== this.from || dates.to !== this.to) {
      Object.assign(this, dates); this.syncControls(); this.saveState();
    }
    return this.load({ automatic: true });
  }

  selectTab(tab) {
    if (!["upcoming", "recent"].includes(tab) || this.disposed) return;
    let request = null;
    if (tab !== this.tab) {
      this.tab = tab; this.sortKey = "date"; this.sortDirection = tab === "recent" ? "desc" : "asc";
      Object.assign(this, calendarDateRange(tab, this.feedRefresh.instant()));
      this.syncControls(); this.saveState(); this.scrollToTop();
      request = this.load();
    }
    this.element.querySelector(`[data-splits-tab="${tab}"]`).focus({ preventScroll: true });
    return request;
  }

  async load({ automatic = false } = {}) {
    this.request?.abort();
    const sequence = ++this.requestSequence;
    const requestedAt = this.feedRefresh.instant();
    const sameRange = this.loadedRange === this.rangeKey();
    const previousContent = JSON.stringify({ items: this.items, metadata: this.metadata });
    this.backgroundLoading = automatic && sameRange && !this.error;
    if (!this.backgroundLoading) { this.items = []; this.metadata = null; this.loadedRange = null; }
    this.loading = true; this.error = null;
    let unchanged = false;
    if (!automatic) this.clearActionError();
    if (this.backgroundLoading) { this.element.setAttribute("aria-busy", "true"); this.updateResultCount(); }
    else this.renderResults();
    try {
      validateDateRange(this.from, this.to);
      const request = new AbortController(); this.request = request;
      const range = this.rangeKey();
      const source = this.tab === "recent" ? this.recentDataSource : this.dataSource;
      const result = await source.load({
        from: this.from, to: this.to, signal: request.signal,
        onProgress: ({ loaded, total }) => {
          if (sequence === this.requestSequence && this.opened && !this.backgroundLoading) this.setCount(`Loading ${loaded.toLocaleString()} of ${total.toLocaleString()} splits…`);
        }
      });
      if (sequence !== this.requestSequence || this.disposed || !this.opened) return;
      unchanged = automatic && sameRange && previousContent === JSON.stringify(result);
      this.items = result.items; this.metadata = result.metadata;
      this.loadedRange = range;
    } catch (error) {
      if (sequence !== this.requestSequence || this.disposed || error?.name === "AbortError") return;
      this.items = []; this.metadata = null; this.loadedRange = null;
      this.error = error instanceof Error ? error.message : "Splits could not be loaded. Please try again.";
    } finally {
      if (sequence === this.requestSequence && !this.disposed) {
        this.loading = false; this.backgroundLoading = false; this.request = null;
        if (unchanged) { this.element.setAttribute("aria-busy", "false"); this.updateResultCount(); }
        else {
          const scroll = this.element.querySelector("[data-upcoming-splits-table-scroll]");
          const position = automatic && sameRange ? { top: scroll.scrollTop, left: scroll.scrollLeft } : null;
          this.renderResults();
          if (position) { scroll.scrollTop = position.top; scroll.scrollLeft = position.left; }
        }
        this.feedRefresh.markRefreshed(requestedAt);
      }
    }
  }

  renderResults() {
    if (!this.opened) return;
    const state = this.element.querySelector("[data-upcoming-splits-state]");
    const scroll = this.element.querySelector("[data-upcoming-splits-table-scroll]");
    const tbody = this.element.querySelector("[data-upcoming-splits-rows]");
    this.element.setAttribute("aria-busy", String(this.loading));
    state.hidden = false; scroll.hidden = true;
    this.element.querySelector("[data-upcoming-splits-retry]").hidden = !this.error;
    if (this.loading && !this.backgroundLoading || this.error) {
      tbody.replaceChildren();
      this.element.querySelector("[data-upcoming-splits-state-title]").textContent = this.loading ? `Loading ${this.tab} splits` : "Unable to load splits";
      this.element.querySelector("[data-upcoming-splits-state-message]").textContent = this.loading ? "Checking all results in this date range…" : this.error;
      this.setCount(this.loading ? "Loading splits…" : "Splits unavailable");
      return;
    }
    const filtered = sortSplits(this.items, this);
    this.updateResultCount(filtered);
    if (!filtered.length) {
      tbody.replaceChildren();
      this.element.querySelector("[data-upcoming-splits-state-title]").textContent = this.tab === "recent" ? "No recent splits found" : "No upcoming splits found";
      this.element.querySelector("[data-upcoming-splits-state-message]").textContent = `No announced effective dates are available for the ${this.tab === "recent" ? "last" : "next"} 30 days. Coverage is partial.`;
    } else {
      state.hidden = true; scroll.hidden = false;
      const fragment = document.createDocumentFragment();
      for (const item of filtered) fragment.append(this.createRow(item));
      tbody.replaceChildren(fragment);
    }
    for (const header of this.element.querySelectorAll("[data-upcoming-splits-sort-header]")) {
      const selected = header.dataset.upcomingSplitsSortHeader === this.sortKey;
      header.setAttribute("aria-sort", selected ? (this.sortDirection === "asc" ? "ascending" : "descending") : "none");
      header.querySelector("button span").textContent = selected ? (this.sortDirection === "asc" ? "↑" : "↓") : "";
    }
  }

  createRow(item) {
    const row = node("tr"); row.dataset.upcomingSplitsId = item.id;
    const date = node("td", "", "upcoming-earnings-date");
    const time = node("time", formatDate(item.effectiveDate)); time.dateTime = item.effectiveDate; date.append(time);
    const company = node("td");
    const chart = node("button", item.symbol, "upcoming-earnings-symbol");
    chart.type = "button"; chart.dataset.upcomingSplitsChart = item.id;
    chart.setAttribute("aria-label", `Open chart for ${item.symbol}`);
    chart.title = `Open ${displayCompanyName(item.companyName)} chart`;
    chart.disabled = this.pendingCharts.has(item.id);
    company.append(chart, node("span", displayCompanyName(item.companyName), "upcoming-earnings-company"));
    const ratio = node("td", "", "upcoming-splits-ratio");
    const displayed = normalizeSplitRatio(item);
    const sourceRatio = `${String(item.numerator)}:${String(item.denominator)}`;
    const reported = node("span", displayed.label);
    reported.setAttribute("aria-label", `${displayed.numerator} new shares for ${displayed.denominator} old shares. Source ratio ${sourceRatio}. ${DATE_NOTE}. ${item.ratioNote}`);
    reported.title = `Source ratio: ${sourceRatio}. ${DATE_NOTE}. ${item.ratioNote}`;
    ratio.append(reported);
    row.append(date, company, ratio);
    return row;
  }

  async openChart(id, button) {
    const item = this.items.find((entry) => entry.id === id);
    if (!item || this.pendingCharts.has(id)) return;
    this.pendingCharts.add(id); button.disabled = true; this.clearActionError();
    try { await this.onOpenChart(item); }
    catch (error) {
      if (this.disposed) return;
      const message = error instanceof Error && error.message ? error.message : `The ${item.symbol} chart could not be opened. Please try again.`;
      const alert = this.element.querySelector("[data-upcoming-splits-action-error]");
      alert.textContent = message; alert.hidden = false; this.announce(message);
    } finally {
      this.pendingCharts.delete(id); if (!this.disposed) button.disabled = false;
    }
  }

  updateResultCount(filtered = sortSplits(this.items, this)) {
    this.setCount(`${filtered.length.toLocaleString()} ${filtered.length === 1 ? "result" : "results"} · ${this.tab === "recent" ? "Last" : "Next"} 30 days${this.backgroundLoading ? " · Checking for updates…" : ""}`);
  }
  setCount(message) { this.element.querySelector("[data-upcoming-splits-count]").textContent = message; }
  clearActionError() { this.element.querySelector("[data-upcoming-splits-action-error]").hidden = true; }
  scrollToTop() { this.element.querySelector("[data-upcoming-splits-table-scroll]").scrollTop = 0; }
  rangeKey() { return `${this.tab}/${this.from}/${this.to}`; }
  syncControls() {
    for (const tab of this.element.querySelectorAll("[data-splits-tab]")) {
      const selected = tab.dataset.splitsTab === this.tab;
      tab.setAttribute("aria-selected", String(selected)); tab.tabIndex = selected ? 0 : -1;
    }
    this.element.querySelector("[data-splits-panel]").setAttribute("aria-labelledby", `${this.tabIdPrefix}-${this.tab}`);
    this.element.querySelector("[data-upcoming-splits-table-scroll]").setAttribute("aria-label", this.tab === "recent" ? "Recent splits results" : "Upcoming splits results");
    this.element.querySelector("[data-splits-caption]").textContent = `${this.tab === "recent" ? "Recent" : "Upcoming"} ${DATE_NOTE}. Ratios show new shares to old shares. Open a symbol to view its chart.`;
  }
  assignPreferences(state) {
    this.tab = state.version === VERSION && state.tab === "recent" ? "recent" : "upcoming";
    Object.assign(this, calendarDateRange(this.tab));
    this.sortKey = state.version === VERSION && Object.hasOwn(SORT_LABELS, state.sortKey) ? state.sortKey : "date";
    this.sortDirection = state.version === VERSION && ["asc", "desc"].includes(state.sortDirection)
      ? state.sortDirection : this.tab === "recent" ? "desc" : "asc";
  }
  defaultGeometry() {
    const width = Math.min(960, this.surface.clientWidth - 16);
    const height = Math.min(620, this.surface.clientHeight - 16);
    return { x: Math.max(0, (this.surface.clientWidth - width) / 2), y: Math.max(0, Math.min(40, this.surface.clientHeight - height)), width: Math.max(0, width), height: Math.max(0, height) };
  }
  snapshotState() {
    return { version: VERSION, open: this.opened, geometry: this.opened ? this.floating.snapshot() : this.savedState.geometry,
      stackOrder: this.stackOrder, tab: this.tab, sortKey: this.sortKey, sortDirection: this.sortDirection };
  }
  saveState() {
    this.savedState = { version: VERSION, ...this.snapshotState() };
    if (this.storageKey) { try { localStorage.setItem(this.storageKey, JSON.stringify(this.savedState)); } catch { /* The tool remains usable without storage. */ } }
    this.onStateChange(this.snapshotState());
  }
  setStorageKey(key) {
    const next = String(key || "").trim();
    if (!next) throw new TypeError("Upcoming splits storage requires a product-qualified key.");
    this.storageKey = next; this.savedState = readState(next);
    this.assignPreferences(this.savedState); this.syncControls();
  }
  restoreState(state) {
    if (!state || typeof state !== "object") state = this.savedState;
    this.assignPreferences(state); this.syncControls();
    this.savedState = { version: VERSION, geometry: validGeometry(state.geometry) ? state.geometry : this.savedState.geometry };
    if (validStack(state.stackOrder)) { this.stackOrder = state.stackOrder; this.element.style.zIndex = String(this.stackOrder); }
    if (state.open === true) {
      if (!this.opened) this.open({ restoreFocus: false });
      else {
        if (validGeometry(this.savedState.geometry)) this.floating.applyGeometry(this.savedState.geometry);
        this.floating.constrainToSurface();
        if (this.loadedRange !== this.rangeKey()) this.load(); else this.renderResults();
      }
    } else if (this.opened) this.close({ restoreFocus: false });
  }
  dispose() {
    this.disposed = true; this.requestSequence += 1; this.request?.abort();
    this.feedRefresh.dispose();
    this.abortController.abort(); this.floating.dispose(); this.element.remove();
    this.launcher.setAttribute("aria-pressed", "false");
  }
}

function node(tag, text = "", className = "") { const element = document.createElement(tag); element.textContent = text; if (className) element.className = className; return element; }
function readState(key) { if (!key) return {}; try { const value = JSON.parse(localStorage.getItem(key) || "{}"); return [1, VERSION].includes(value?.version) ? value : {}; } catch { return {}; } }
function validGeometry(value) { return value && ["x", "y", "width", "height"].every((key) => Number.isFinite(value[key])) && value.width > 0 && value.height > 0; }
function validStack(value) { return Number.isFinite(value) && value > 0; }
function formatDate(value) { const parsed = new Date(`${value}T00:00:00Z`); return Number.isFinite(parsed.getTime()) ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(parsed) : String(value || ""); }

export function sortSplits(items, { tab = "upcoming", sortKey = "date", sortDirection = tab === "recent" ? "desc" : "asc" } = {}) {
  const key = Object.hasOwn(SORT_LABELS, sortKey) ? sortKey : "date";
  const direction = sortDirection === "desc" ? -1 : 1;
  const compare = (a, b) => String(a || "").localeCompare(String(b || ""), "en", { numeric: true });
  const value = (item) => ({ date: item.effectiveDate, symbol: item.symbol })[key];
  const pairs = new Map();
  if (key === "ratio") for (const item of items) {
    const pair = normalizeSplitRatio(item);
    pairs.set(item, [BigInt(pair.numerator), BigInt(pair.denominator)]);
  }
  const compareRatio = (a, b) => {
    const [leftNew, leftOld] = pairs.get(a); const [rightNew, rightOld] = pairs.get(b);
    if (leftNew !== rightNew) return leftNew < rightNew ? -1 : 1;
    return leftOld === rightOld ? 0 : leftOld < rightOld ? -1 : 1;
  };
  return [...items].sort((a, b) => (key === "ratio" ? compareRatio(a, b) : compare(value(a), value(b))) * direction
    || compare(a.effectiveDate, b.effectiveDate) || compare(a.symbol, b.symbol) || compare(a.id, b.id));
}
